import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { PersonStickyIcon, PersonStickyIconEng } from "ui/icons"

import colors from "theme/colors"
import { Desktop, Mobile, NewText, useIsDesktop } from "ui"
import { httpsImage } from "utils/utils"

const StyledImageWrapper = styled.div<{ noHover?: boolean }>`
  position: relative;
  height: 370px;
  margin-right: 40px;
  overflow: hidden;
  width: 300px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 220px;
    height: 280px;
    &:hover .hover-content {
      transform: ${(props) => (props.noHover ? "" : "translateY(0)")};
    }
    :focus .hover-content {
      transform: ${(props) => (props.noHover ? "" : "translateY(0)")};
    }
    img {
      object-fit: cover;
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 300px;
    height: 420px;
    img {
      object-fit: cover;
    }
  }
  &:nth-last-child(n + 1) {
    margin-right: 0;
  }
`

const StyledTextWrapper = styled.div`
  position: absolute;
  bottom: 0;
  padding: 16px;
  p {
    margin: 0px;
    max-width: 230px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 8px;
    max-width: 200px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding: 16px;
    max-width: 250px;
  }
`

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: flex-start;
  }
`

const StyledLine = styled.div<{ $color: string }>`
  width: 16px;
  height: 2px;
  background: ${(p) => p.$color};
  margin-top: 12px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 6px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-top: 12px;
  }
  margin-right: 5px;
`

const StyledSvgWrapper = styled.div`
  position: absolute;
  bottom: 0;
  height: 98px;
  left: 0px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 65px;
    left: -12px;
    svg {
      height: 65px;
      width: 212px;
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 98px;
    left: 0px;
    svg {
      height: 98px;
      width: 282px;
    }
  }
`
const Blurred = styled.div<{ en?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  backdrop-filter: blur(10px);
  mask: ${(props) =>
    props.en
      ? `url("/icons/personmasken.svg")`
      : `url("/icons/personmask.svg")`};
  background: inherit; /* Or specific color/background if needed */
  height: 98px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 65px;
    left: -12px;
    width: 212px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 98px;
    left: -12px;
    width: 282px;
  }
`

const HoverContentWrapper = styled.div<{ $color: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: ${(props) => props.$color}E6;
  backdrop-filter: blur(15px);
  height: 100%;
  transform: translateY(100%);
  transition: all 0.5s ease-in-out;
  overflow: auto;
`

const StyledMobileTextWrapper = styled.div<{ $background: string | undefined }>`
  height: ${(p) => (p.$background === colors.yellow ? "100px" : "370px")};
  width: 100%;
  background: ${(p) => p.$background};
  padding: 24px 20px;
  overflow: auto;
  box-sizing: border-box;
`

const StyledDescription = styled.div`
  p:last-child {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-left: 24px;
  }
`

const StyledWrapper = styled.div``

const StyledRole = styled.div`
  margin-bottom: 20px;
`

type PersonItemProps = {
  title: string
  image: string
  description: string
  role: string
  freeText?: string
  color: string
  isYellow?: boolean
  noHover?: boolean
  localeLang: string
}

function PersonItem({
  title,
  role,
  color,
  isYellow,
  image,
  description,
  noHover,
  localeLang,
}: PersonItemProps) {
  const locale = useLocale()
  const isDesktop = useIsDesktop()
  return (
    <StyledWrapper>
      <StyledImageWrapper noHover={noHover} tabIndex={0} aria-label={`${title}, ${role}`} role="group">
        <Image
          src={image ? httpsImage(image) : "/images/imageplaceholder.png"}
          alt=""
          fill
          priority
          quality={85}
        />
        <Desktop>
          <Blurred en={localeLang === "en"}></Blurred>
          <StyledSvgWrapper>
            {locale === "en" ? (
              <PersonStickyIconEng
                color={isYellow ? colors.yellow : colors.blue}
              />
            ) : (
              <PersonStickyIcon
                color={isYellow ? colors.yellow : colors.blue}
              />
            )}
          </StyledSvgWrapper>
          <StyledTextWrapper>
            <StyledTitle>
              <StyledLine $color={isYellow ? colors.black : colors.white} />
              <NewText
                component={"h3"}
                largeDesktopFontSize={20}
                desktopFontSize={14}
                desktopLineHeight={"21px"}
                mobileFontSize={18}
                largeDesktopLineHeight={"30px"}
                mobileLineHeight={"25px"}
                desktopFontWeight={500}
                color={isYellow ? colors.black : colors.white}
              >
                {title}
              </NewText>
            </StyledTitle>
            <NewText
              component={"p"}
              largeDesktopFontSize={16}
              largeDesktopLineHeight={"24px"}
              desktopFontSize={12}
              desktopLineHeight={"18px"}
              desktopFontWeight={400}
              color={isYellow ? colors.black : colors.white}
              marginLeft={"21px"}
              smallDesktopFontSize={12}
              smallDesktopLineHeight="18px"
            >
              {role}
            </NewText>
          </StyledTextWrapper>
        </Desktop>
        <HoverContentWrapper
          $color={isYellow ? colors.yellow : colors.darkBlue}
          className="hover-content"
        >
          <StyledTitle>
            <StyledLine $color={isYellow ? colors.black : colors.white} />
            <NewText
              aria-hidden={true}
              component={"h3"}
              largeDesktopFontSize={24}
              mobileFontSize={18}
              largeDesktopLineHeight={"36px"}
              mobileLineHeight={"25px"}
              desktopFontWeight={500}
              color={isYellow ? colors.black : colors.white}
              desktopFontSize={18}
              desktopLineHeight="25px"
            >
              {title}
            </NewText>
          </StyledTitle>
          <StyledRole>
            <NewText
              aria-hidden={true}
              component={"div"}
              largeDesktopFontSize={18}
              largeDesktopLineHeight={"27px"}
              desktopFontWeight={400}
              mobileFontSize={16}
              mobileLineHeight={"24px"}
              color={isYellow ? colors.black : colors.white}
              marginLeft={"21px"}
              desktopFontSize={14}
              desktopLineHeight="20px"
            >
              {role}
            </NewText>
          </StyledRole>
          <StyledDescription>
            <NewText
              component={"div"}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              largeDesktopLineHeight={"20px"}
              mobileLineHeight={"20px"}
              color={color}
              desktopFontSize={12}
              desktopLineHeight="18px"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </StyledDescription>
        </HoverContentWrapper>
      </StyledImageWrapper>
      <Mobile>
        <StyledMobileTextWrapper
          $background={isYellow ? colors.yellow : colors.blue}
        >
          <StyledTitle>
            <StyledLine $color={isYellow ? colors.black : colors.white} />
            <NewText
              component={"div"}
              desktopFontSize={20}
              mobileFontSize={18}
              desktopLineHeight={"30px"}
              mobileLineHeight={"25px"}
              desktopFontWeight={500}
              color={isYellow ? colors.black : colors.white}
              smallDesktopFontSize={14}
              smallDesktopLineHeight="21px"
            >
              {title}
            </NewText>
          </StyledTitle>
          <StyledRole>
            <NewText
              component={"div"}
              desktopFontSize={18}
              desktopLineHeight={"27px"}
              desktopFontWeight={400}
              mobileFontSize={16}
              mobileLineHeight={"24px"}
              color={isYellow ? colors.black : colors.white}
              marginLeft={isDesktop ? "21px" : ""}
              smallDesktopFontSize={14}
              smallDesktopLineHeight="20px"
            >
              {role}
            </NewText>
          </StyledRole>
          <StyledDescription>
            <NewText
              component={"div"}
              desktopFontSize={18}
              mobileFontSize={14}
              desktopLineHeight={"27px"}
              mobileLineHeight={"20px"}
              color={color}
              smallDesktopFontSize={14}
              smallDesktopLineHeight="21px"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </StyledDescription>
        </StyledMobileTextWrapper>
      </Mobile>
    </StyledWrapper>
  )
}

export default PersonItem
